import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Oblique Design System — MoreMenu
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Reusable 3-dots (more_horiz) context menu.
 * Uses createPortal to ensure it's never clipped by parent overflow containers.
 * 
 * Usage:
 *   import { MoreMenu, MoreMenuItem } from '@/core/oblique-ui'
 *   
 *   <MoreMenu>
 *       <MoreMenuItem icon="edit" label="Edit" onClick={() => {}} />
 *       <MoreMenuItem icon="delete" label="Delete" color="#d4555e" onClick={() => {}} />
 *   </MoreMenu>
 */

export function MoreMenu({ children, align = 'right', className = '' }) {
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                menuRef.current && !menuRef.current.contains(event.target) &&
                buttonRef.current && !buttonRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const getCoords = () => {
        if (!buttonRef.current) return { top: 0, left: 0, right: 0 };
        const rect = buttonRef.current.getBoundingClientRect();
        return {
            top: rect.bottom + 4,
            left: align === 'right' ? rect.right : rect.left,
            isRight: align === 'right'
        };
    };

    return (
        <div className={`relative shrink-0 ${className}`}>
            <button
                ref={buttonRef}
                onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
                className={`w-7 h-7 rounded bg-transparent border-none text-[var(--text-tertiary)] flex items-center justify-center cursor-pointer transition-colors ${isOpen ? 'bg-[var(--bg-muted)] text-[var(--text-secondary)]' : 'hover:bg-[var(--bg-muted)] hover:text-[var(--text-secondary)]'}`}
            >
                <ObliqueIcon name="more_horiz" size={18} />
            </button>

            {typeof document !== 'undefined' && createPortal(
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            ref={menuRef}
                            layout
                            initial={{ opacity: 0, y: 4, x: getCoords().isRight ? '-100%' : 0, scale: 0.8, filter: 'blur(10px)', borderRadius: '24px' }}
                            animate={{ opacity: 1, y: 0, x: getCoords().isRight ? '-100%' : 0, scale: 1, filter: 'blur(0px)', borderRadius: '12px' }}
                            exit={{ opacity: 0, y: 4, x: getCoords().isRight ? '-100%' : 0, scale: 0.8, filter: 'blur(10px)', borderRadius: '24px' }}
                            transition={{ type: 'spring', stiffness: 400, damping: 30, mass: 0.8 }}
                            style={{ 
                                top: getCoords().top, 
                                left: getCoords().left,
                                transformOrigin: align === 'right' ? 'top right' : 'top left'
                            }}
                            className="fixed bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border border-white/20 dark:border-gray-800 shadow-2xl z-[var(--z-popover,5000)] min-w-[160px] p-1 flex flex-col"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Pass setIsOpen to children so they can close the menu on click */}
                            {React.Children.map(children, child => {
                                if (React.isValidElement(child)) {
                                    return React.cloneElement(child, { 
                                        onClose: () => setIsOpen(false) 
                                    });
                                }
                                return child;
                            })}
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </div>
    );
}

export function MoreMenuItem({ icon, label, color = 'var(--text-primary)', onClick, onClose }) {
    const isDanger = color === '#d4555e' || color === 'var(--danger)';
    
    const handleClick = (e) => {
        if (onClick) onClick(e);
        if (onClose) onClose();
    };

    return (
        <div
            onClick={handleClick}
            className={`flex items-center gap-2.5 px-2.5 py-1.5 text-[13px] rounded-md cursor-pointer transition-colors ${isDanger ? 'text-[#d4555e] hover:bg-red-50 dark:hover:bg-red-950/30' : 'text-[var(--text-primary)] hover:bg-[var(--bg-muted)]'}`}
        >
            {icon && (
                <ObliqueIcon 
                    name={icon} 
                    size={16} 
                    color={isDanger ? color : 'var(--text-tertiary)'} 
                />
            )}
            <span className={isDanger ? 'font-medium' : ''}>{label}</span>
        </div>
    );
}

export function MoreMenuDivider() {
    return <div className="border-t border-[var(--border-subtle)] my-1" />;
}
